import { type FC, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { graphql, fetchQuery, createFragmentContainer, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { useIntl, FormattedMessage, FormattedDate } from 'dibs-react-intl';
import { trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { StarRating } from 'dibs-elements/exports/StarRating';
import { getLang } from 'dibs-intl/exports/languages';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { Link } from 'dibs-elements/exports/Link';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import TranslationToggle, { type TranslationState } from 'dibs-elements/exports/TranslationToggle';
import { readLessButton, readMoreButton } from '../ReadMoreButton/ReadMoreButton';

const PhotosModal = lazy(() =>
    import('dibs-seller-reviews-modal/src/SellerReviewsPhotosModal').then(mod => ({
        default: mod.SellerReviewsPhotosModal,
    }))
);

import { type Review_review$data } from './__generated__/Review_review.graphql';
import { type ReviewTextQuery } from './__generated__/ReviewTextQuery.graphql';

import styles from './Review.scss';

type Props = {
    review: Review_review$data | null | undefined;
    showOrderPhoto?: boolean;
    translationState?: TranslationState;
    noTabs?: boolean;
    isLastRow?: boolean;
    hideDetailsSeparator?: boolean;
};

const reviewTranslationQuery = graphql`
    query ReviewTextQuery($text: String!, $target: String!) {
        viewer {
            translateText(text: $text) {
                text(target: $target)
            }
        }
    }
`;

export const ReviewBase: FC<Props> = ({
    review,
    showOrderPhoto = false,
    translationState: externalTranslationState,
    noTabs,
    isLastRow,
    hideDetailsSeparator,
}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();

    const [internalTranslationState, setInternalTranslationState] =
        useState<TranslationState>('HIDDEN');
    const [translating, setTranslating] = useState(false);
    const [translatedText, setTranslatedText] = useState('');

    const translationState = noTabs ? internalTranslationState : externalTranslationState;
    const isTranslated = translatedText && translationState === 'SHOW_TRANSLATION';

    const fetchTranslation = useCallback(async (): Promise<void> => {
        const freetext = review?.publicNote?.freetext;
        if (freetext && !translatedText) {
            setTranslating(true);
            const data = await fetchQuery<ReviewTextQuery>(environment, reviewTranslationQuery, {
                text: freetext,
                target: getLang(intl.locale),
            }).toPromise();
            if (data?.viewer?.translateText?.text) {
                setTranslatedText(data.viewer.translateText.text);
            }
            setTranslating(false);
        }
    }, [review, translatedText, environment, intl.locale]);

    const onTranslate = async (): Promise<void> => {
        if (!translating && noTabs) {
            await fetchTranslation();
            setInternalTranslationState(isTranslated ? 'SHOW_ORIGINAL' : 'SHOW_TRANSLATION');
        }
    };

    useEffect(() => {
        if (noTabs) {
            const language = review?.publicNote?.language;
            if (language && language !== getLang(intl.locale)) {
                setInternalTranslationState('SHOW_ORIGINAL');
            } else {
                setInternalTranslationState('HIDDEN');
            }
        }
    }, [noTabs, review, intl.locale]);

    useEffect(() => {
        if (!noTabs && externalTranslationState === 'SHOW_TRANSLATION') {
            fetchTranslation();
        }
    }, [noTabs, externalTranslationState, fetchTranslation]);

    const onItemImgClick = useCallback(
        function () {
            const item = review?.item;
            const seller = item?.seller;
            const { ECOM_PRODUCT_CLICK } = trackingConstants;
            const { EVENT_SELECT_ITEM } = eventNameConstants;

            // istanbul ignore else
            if (item && seller) {
                trackEcommerce({
                    type: ECOM_PRODUCT_CLICK,
                    eventName: EVENT_SELECT_ITEM,
                    actionField: {
                        list: 'sellerReviews',
                        itemId: item.serviceId,
                    },
                    products: [
                        {
                            id: item.serviceId,
                            name: item.title,
                            brand: seller.serviceId,
                            dimension83: item.contemporaryTrackingString,
                        },
                    ],
                });
            }
        },
        [review]
    );

    const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);

    if (!review) {
        return (
            <div className={styles.loadingWrapper}>
                <Spinner dark />
            </div>
        );
    }

    const {
        item,
        overallExperience,
        publicNote,
        completedDate,
        buyerFirstName,
        buyerShippingAddress: shippingAddress,
        orderPhotos,
    } = review;
    const rating = overallExperience?.rating;

    let buyerLocation = null;
    if (shippingAddress && shippingAddress.country === 'US') {
        buyerLocation = shippingAddress.displayState;
    } else if (shippingAddress) {
        buyerLocation = shippingAddress.displayCountry;
    }
    const { freetext: publicNoteText, visible: isPublicNoteVisible } = publicNote || {};
    const { firstPhotoWebPath: itemImgSrc, linkData } = item || {};
    //show only latest photo
    const orderPhotoUrl = orderPhotos?.[0]?.smallPath;

    // temp fix: do not link to pdp's from review modal.
    // long term fix: do not link to items that have been deleted and no longer have a valid pdp
    // work will be completed with VERTICALS-1216 when we return islocalizedPdpUrlRedirect from itemType
    const islocalizedPdpUrlRedirect = true;
    const hasImageData = linkData && itemImgSrc;

    let image: JSX.Element | null = null;
    if (hasImageData) {
        if (islocalizedPdpUrlRedirect) {
            image = <img alt={item?.title || ''} className={styles.image} src={itemImgSrc} />;
        } else {
            image = (
                <SeoLink
                    dataTn="seller-review-modal-pdp-link"
                    onClick={onItemImgClick}
                    target="_blank"
                    linkData={linkData}
                >
                    <img alt={item?.title || ''} className={styles.image} src={itemImgSrc} />
                </SeoLink>
            );
        }
    }

    const showSeparator = !(isLastRow || hideDetailsSeparator);

    if (noTabs) {
        return (
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsStart,
                    dibsCss.justifyBetween,
                    dibsCss.gapLarge,
                    !hideDetailsSeparator && dibsCss.pbMedium,
                    showSeparator && dibsCss.mbMedium,
                    showSeparator && dibsCss.borderB,
                    showSeparator && dibsCss.borderBSolid,
                    showSeparator && dibsCss.borderBDolphin
                )}
            >
                <div className={classnames(dibsCss.flexGrow, dibsCss.minW0)}>
                    <div
                        className={classnames(
                            styles.details,
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.textSatan,
                            dibsCss.sassyFontBodySmall,
                            dibsCss.mbXsmall
                        )}
                    >
                        {rating && (
                            <span className={dibsCss.mrSmaller}>
                                <StarRating rating={rating} size="x-small" inline />
                            </span>
                        )}
                        {completedDate && (
                            <>
                                {buyerFirstName && (
                                    <>
                                        <span>{buyerFirstName}</span>
                                        <span className={dibsCss.mxXsmall}>|</span>
                                    </>
                                )}
                                <div>
                                    <FormattedDate
                                        value={completedDate}
                                        year="numeric"
                                        month="long"
                                    />
                                </div>
                            </>
                        )}
                        {buyerLocation && <div>, {buyerLocation}</div>}
                    </div>
                    <div className={styles.review}>
                        {publicNoteText && isPublicNoteVisible ? (
                            <ExpandingAreaWithReadMore
                                wrapperClass={dibsCss.sassyFontBodyMedium}
                                lineClamp={3}
                                showGradient={false}
                                dataTn="seller-review-read-more"
                                readMore={readMoreButton}
                                readLess={readLessButton}
                                buttonClass={styles.noUnderline}
                                disableScrollOnCollapse
                            >
                                <FormattedMessage
                                    id="abf.Review.publicNote"
                                    defaultMessage="{hasFirstName, select, 
                                        true {{name}: {message}} 
                                        other {{message}}
                                    }"
                                    values={{
                                        hasFirstName: false,
                                        name: buyerFirstName ? buyerFirstName : '',
                                        message: (isTranslated
                                            ? translatedText
                                            : publicNoteText
                                        )?.trim(),
                                    }}
                                />
                            </ExpandingAreaWithReadMore>
                        ) : (
                            <div className={styles.publicNotePlaceholder}>
                                <FormattedMessage
                                    id="Review.noPublicNote"
                                    defaultMessage="{name} did not provide any comments about their review"
                                    values={{
                                        name: buyerFirstName ? (
                                            buyerFirstName
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="This customer"
                                                id="Review.customerNamePlaceholder"
                                            />
                                        ),
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    {internalTranslationState !== 'HIDDEN' && (
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.gapSmall,
                                dibsCss.mtSmaller
                            )}
                        >
                            <TranslationToggle
                                currentState={internalTranslationState}
                                onClick={onTranslate}
                            />
                            {translating && <Spinner black size="tiny" />}
                        </div>
                    )}
                    {showOrderPhoto && orderPhotoUrl && (
                        <div
                            className={classnames(
                                dibsCss.inlineBlock,
                                dibsCss.mtXsmall,
                                dibsCss.overflowHidden,
                                dibsCss.roundedXxsmall
                            )}
                            style={{ width: 108, height: 108 }}
                        >
                            <Link
                                underline="none"
                                onClick={() => setIsPhotosModalOpen(true)}
                                ariaLabel="Open order photo"
                                className={classnames(dibsCss.block, dibsCss.wFull, dibsCss.hFull)}
                            >
                                <img
                                    src={orderPhotoUrl}
                                    alt=""
                                    className={classnames(
                                        dibsCss.block,
                                        dibsCss.objectCover,
                                        dibsCss.objectCenter,
                                        dibsCss.wFull,
                                        dibsCss.hFull
                                    )}
                                />
                            </Link>
                        </div>
                    )}
                </div>
                {hasImageData && (
                    <div className={dibsCss.flexNone}>
                        <div
                            className={classnames(
                                dibsCss.pSmall,
                                dibsCss.roundedFull,
                                dibsCss.border,
                                dibsCss.borderSolid,
                                dibsCss.borderMoonstone
                            )}
                        >
                            <img
                                alt={item?.title || ''}
                                className={classnames(
                                    dibsCss.wLarger,
                                    dibsCss.hLarger,
                                    dibsCss.roundedFull,
                                    dibsCss.objectCover,
                                    dibsCss.block
                                )}
                                src={itemImgSrc || ''}
                            />
                        </div>
                    </div>
                )}
                {isPhotosModalOpen && item?.seller?.serviceId && (
                    <Suspense fallback={null}>
                        <PhotosModal
                            isOpen
                            onClose={() => setIsPhotosModalOpen(false)}
                            sellerId={item.seller.serviceId}
                            noTabs
                        />
                    </Suspense>
                )}
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            {image}
            <div className={styles.details}>
                {rating && <StarRating rating={rating} size="x-small" />}
                {completedDate && (
                    <div className={styles.buyerDate}>
                        <FormattedDate value={completedDate} year="numeric" month="long" />
                    </div>
                )}
                <div className={styles.buyerLocation}>{buyerLocation}</div>
            </div>
            <div className={styles.review}>
                {publicNoteText && isPublicNoteVisible ? (
                    <div className={styles.publicNote}>
                        <FormattedMessage
                            id="abf.Review.publicNote"
                            defaultMessage="{hasFirstName, select, 
                                true {{name}: {message}} 
                                other {{message}}
                            }"
                            values={{
                                hasFirstName: !!buyerFirstName,
                                name: buyerFirstName ? buyerFirstName : '',
                                message: (isTranslated ? translatedText : publicNoteText)?.trim(),
                            }}
                        />
                    </div>
                ) : (
                    <div className={styles.publicNotePlaceholder}>
                        <FormattedMessage
                            id="Review.noPublicNote"
                            defaultMessage="{name} did not provide any comments about their review"
                            values={{
                                name: buyerFirstName ? (
                                    buyerFirstName
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="This customer"
                                        id="Review.customerNamePlaceholder"
                                    />
                                ),
                            }}
                        />
                    </div>
                )}
            </div>
            {showOrderPhoto && orderPhotoUrl && (
                <div className={styles.orderImageWrapper}>
                    <img src={orderPhotoUrl} alt="" className={styles.orderImage} />
                </div>
            )}
        </div>
    );
};

export const Review = createFragmentContainer(ReviewBase, {
    review: graphql`
        fragment Review_review on OrderFeedback {
            serviceId
            completedDate
            buyerFirstName
            orderPhotos {
                smallPath
            }
            buyerShippingAddress {
                displayState
                country
                displayCountry
            }
            overallExperience {
                rating
            }
            publicNote {
                freetext
                visible
                ... on StandardQuestion {
                    language
                }
            }
            item {
                serviceId
                title
                contemporaryTrackingString
                firstPhotoWebPath(size: thumb)
                seller {
                    serviceId
                }
                linkData {
                    ...SeoLink_linkData
                }
            }
        }
    `,
});
